// Aanar user manual — friendly & conversational. Single source of truth used
// by both the in-app /help page and the /manual print/download route.
// Each section becomes a sidebar entry; keep h1 as the section title.

export const MANUAL_SECTIONS = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: "sparkles",
    body: `
# Welcome to Aanar 👋

Aanar is a paper-trading dashboard for stocks and crypto — with an AI coach,
automated bots, and (optional) live routing through Interactive Brokers. This
manual walks you through every screen. It won't take long — you can master
Aanar in about 15 minutes.

## What you get for free
- **$100,000 virtual cash** to start paper trading immediately
- Real market prices for **stocks** (US equities) and **crypto** (via CoinGecko)
- **AI Coach** grounded in classic trading books (Market Wizards, Reminiscences, O'Neil, Graham, Lynch)
- **Chart Copilot** that narrates any candlestick chart in plain English
- **Bots** you can run 24/7 on any watchlist symbol

## What's optional (advanced)
- Connect **Interactive Brokers** to route real trades from your paper strategies
- **Options chain** for any US-listed ticker with Greeks and IV
- **Alerts** (in-app + push) on price levels, patterns, and bot events
- **Referral program** — invite friends and earn credit

## First 5 minutes
1. Sign in with your email/password (or Face ID on iPhone)
2. Head to **Markets** — tap any asset to see its chart and place your first paper trade
3. Open **Portfolio** to see your positions and P&L
4. Ask the **AI Coach** a question ("Is TSLA overextended?") — it'll quote a wizard
5. When ready, spin up your first **Bot** with a strategy like DCA or Momentum
`,
  },
  {
    id: "paper-trading",
    title: "Paper Trading",
    icon: "wallet",
    body: `
# Paper Trading

Every account starts with **$100,000 virtual cash**. There's no real money at
risk — the whole platform is a sandbox until you connect a real broker.

## Placing a trade
1. Go to **Markets** or **Portfolio** → tap any symbol
2. On the asset page, choose **Buy** or **Sell**
3. Enter quantity (fractional shares supported — e.g. 0.15 BTC or 0.5 AAPL)
4. Optionally attach **exit rules**:
   - **Stop-loss** at a fixed price
   - **Take-profit** at a fixed price
   - **Trailing stop** (%) — moves up as the price climbs
5. Tap **Confirm order**

Your fill hits instantly at the live market price. Aanar simulates a small
random slippage (1-3 basis points) so results feel realistic.

## Cash & buying power
- **Cash balance** — the un-invested dollars in your paper account
- **Total equity** — cash + market value of all holdings
- **Buying power** — how much you can spend on new buys (equal to cash in paper mode)

## Fractional shares
Aanar supports fractional buys for both stocks and crypto. On the buy ticket,
toggle **"Notional"** to enter a dollar amount instead of share count — Aanar
computes the fractional quantity for you.

## Exit rules (SL / TP / trailing)
Once attached, the **background loop** watches live prices every 5 seconds and
auto-fires the exit as soon as your stop or target is hit. You'll get an
in-app notification via the bell in the top bar.
`,
  },
  {
    id: "portfolio",
    title: "Portfolio",
    icon: "briefcase",
    body: `
# Portfolio

The Portfolio page has three tabs:

## 1. Paper (default)
Your Aanar sim holdings — every symbol you've bought, current price,
unrealized P&L, and % of portfolio. Tap any row to open its detail page.

## 2. IBKR Live
Only visible if you've connected Interactive Brokers via MCP. Shows real
positions pulled directly from your IBKR account (equity, cash, buying power,
unrealized P&L per position).

## 3. IBKR Paper
Same as Live, but reads from your IBKR paper account (if you've enabled paper
trading in IBKR).

## Allocation donut
Below the tabs, a donut chart shows how your equity is split across positions.
Tiny slices roll into "Other" so the chart stays readable.

## Heatmap
The green/red heatmap sizes each tile by market value and colors by daily %.
Big green tiles = your best winners today. Big red = today's losers.

## Journal & Trade History
Below the heatmap, every fill is logged in the **Journal**. Add a note or tag
(momentum / mistake / news-driven / etc.) so you can review your patterns
weekly. The **AI Coach** learns from your journal to give you smarter nudges.
`,
  },
  {
    id: "ibkr-live",
    title: "IBKR Live Trading",
    icon: "landmark",
    body: `
# Connecting Interactive Brokers

Aanar can route your paper strategies as real orders through Interactive
Brokers via IBKR's native MCP (Model Context Protocol). This is 100% optional
— skip this section if you only want to paper-trade.

## Requirements
- An active Interactive Brokers account (live or paper)
- Portfolio Analytics enabled in your IBKR account (for the analytics dashboard)

## Setup (2 minutes)
1. Go to **Settings** → **Interactive Brokers**
2. Tap **Connect IBKR** — you'll be redirected to IBKR's login
3. Sign in and approve the connection (uses OAuth 2.1 DCR — no manual API tickets needed)
4. You'll bounce back to Aanar with a green "Connected" badge

## How live routing works
When you place a trade with route = "IBKR", Aanar does **NOT** send the order
directly to the market. Instead, it creates a **draft** in your IBKR account's
"AI Instructions" tab. You then open IBKR mobile or desktop and **manually
tap Confirm** to execute the trade.

This is intentional — it's a safety layer so no algorithm ever fires a real
order without your final human tap.

## Broker Analytics dashboard
Head to **/ibkr** (in the sidebar) for a real IBKR view:
- Net Liquidation, Cash, Buying Power, Unrealized P&L tiles
- **Performance chart** — 1D / 1W / 1M / 3M / YTD / 1Y equity curve
- **Allocation pie** — by asset class from IBKR PA data
- **Working orders** — live view of pending orders sitting in IBKR
- **Trade blotter** — last 7 days of fills

Refreshes every 30 seconds.

## Disconnecting
Settings → Interactive Brokers → **Disconnect**. This revokes Aanar's access
token. Your IBKR account is unaffected — the connection just goes cold.
`,
  },
  {
    id: "bots",
    title: "Automated Bots",
    icon: "bot",
    body: `
# Bots

A bot is an automated trader that runs a strategy on your behalf. Aanar
supports several built-in strategies plus AI-driven ones.

## Creating a bot
1. Go to **Bots** → **+ New Bot**
2. Give it a name (e.g. "AAPL momentum daily")
3. Pick a **strategy**:
   - **DCA** — buy a fixed dollar amount at each interval
   - **Momentum** — buy on breakout, sell on breakdown
   - **Mean Reversion** — buy dips, sell rips (RSI-based)
   - **AI Trader** — Claude-powered decision on every tick
4. Choose your **symbols** (one or many)
5. Set **interval** (how often the bot checks the market — 5s to 1h)
6. Set **duration** — trade count, time window, or "manual" (runs until you stop it)
7. Choose **route** — Sim (paper), IBKR Paper, or IBKR Live
8. Optionally attach **safety rules** (max daily loss, max position size, cooldown)
9. Tap **Start bot**

## Watching a bot run
Tap any bot card to see:
- **Live P&L** and win rate
- **Recent decisions** with the AI reasoning ("Bought AAPL @ $180.23 — RSI oversold + volume confirmation")
- **Active exit rules** if any positions are open
- **Compare panel** — paper P&L vs your IBKR live P&L side by side

## Cloning a bot
Tap the **Copy icon** on any bot card. Aanar duplicates the exact
config — strategy, symbols, safety, interval — as a fresh bot. Great for
running the same idea on a different symbol.

## Stopping / deleting
- **Stop** — halts the bot but keeps its config and history
- **Trash** — permanently deletes the bot

## Kill switch
The **"Stop all bots"** button in the top bar (red) instantly halts every
running bot. Use it when the market turns ugly or before big news.

## Backtesting
Before you start a real bot, run a **backtest** first. On the bot creation
screen, tap **Backtest** to simulate the same strategy on historical data.
You'll see win rate, max drawdown, Sharpe, and equity curve — decide if
it's worth deploying live.
`,
  },
  {
    id: "options",
    title: "Options Trading",
    icon: "layers",
    body: `
# Options

Aanar supports options paper trading on any US-listed ticker.

## Loading a chain
1. Go to **Trade → Options** (or type any ticker into the search bar)
2. Enter a symbol (AAPL, SPY, TSLA, etc.) and hit **Load**
3. Pick an **expiration** — every listed expiry appears as a chip
4. The full chain populates with:
   - Bid / Ask / Last / Volume / Open Interest
   - **Greeks** — Δ (delta), Γ (gamma), Θ (theta), V (vega)
   - **IV** — implied volatility per contract

## Popular tickers
The **Popular chips** row gives you one-tap access to the 12 most-watched
tickers (AAPL, MSFT, GOOGL, AMZN, TSLA, NVDA, META, NFLX, AMD, SPY, QQQ, GLD).
Your last 8 viewed tickers are also saved as **Recent chips**.

## Placing an option trade
Tap any contract → paper Buy or Sell → Confirm. Fill happens at the mid-price.
Your position appears in Portfolio under "Options".

## Coming soon
- Multi-leg builder (spreads, iron condors)
- IV history sparkline per contract
- Real options routing through IBKR (currently paper only)
`,
  },
  {
    id: "ai-coach",
    title: "AI Coach",
    icon: "brain",
    body: `
# AI Coach

Aanar's AI Coach is a chat mentor powered by **Claude Sonnet 5**, grounded in
the wisdom of classic trading books:

- Reminiscences of a Stock Operator
- Market Wizards
- Trading in the Zone
- The Intelligent Investor
- How to Make Money in Stocks
- One Up on Wall Street
- Technical Analysis of the Financial Markets
- And more…

## How to use it
1. Sidebar → **Coach**
2. Ask any trading question in plain English:
   - "Is TSLA overextended right now?"
   - "What would Jesse Livermore do here?"
   - "Explain the head-and-shoulders pattern"
   - "Review my last 5 trades"
3. Coach streams a live response, often quoting the exact book + chapter

## Multi-turn conversations
Coach remembers your session — follow-ups work naturally ("What about SPY?")
without re-explaining context. Sessions are private and never leave your
account.

## Free tier limits
- **Free**: 10 messages / day
- **Pro**: unlimited messages, priority AI, longer memory

## What Coach knows
- Your **positions**, **P&L**, and **journal notes**
- Live prices for anything you ask about
- **Chart patterns** on the assets you view
- Market news relevant to your holdings
`,
  },
  {
    id: "chart-copilot",
    title: "Chart Copilot",
    icon: "line-chart",
    body: `
# Chart Copilot

Chart Copilot is a smaller, chart-focused AI that narrates any candlestick.

## Where to find it
On any **Asset Detail** page (tap a symbol from Markets or Portfolio), scroll
to the candlestick chart. Below it, tap **Copilot**.

## What it does
- Detects patterns: head-and-shoulders, double top/bottom, wedges, triangles, breakouts
- Names the trend (uptrend / downtrend / range)
- Marks key levels: support, resistance, gaps
- Explains volume anomalies
- Suggests a trade idea (with confidence)

## Beginner mode
The first time you use Copilot, it's in **Beginner mode** — plain English,
no jargon. Toggle to **Advanced** once you're comfortable and it'll drop the
hand-holding.

## Share the analysis
Tap the **share icon** to export the annotated chart + narrative as a PNG.
Perfect for posting on X, Discord, or Slack. The PNG watermark says
"aanar · charted".
`,
  },
  {
    id: "alerts",
    title: "Alerts & Notifications",
    icon: "bell",
    body: `
# Alerts

Aanar has three notification systems:

## 1. Price / pattern alerts
- **/alerts** → **+ New alert**
- Choose a symbol, a trigger (above / below / crosses / pattern), and a delivery channel (in-app + push)
- Aanar's streaming loop watches prices tick by tick and fires the moment your trigger hits

## 2. Stop-triggered alerts
Any time a **stop-loss**, **take-profit**, or **trailing stop** fires, Aanar
drops a notification in the bell (top bar). Tap the bell to see the last 10.

You can **tune which stop events notify you** in the bell's gear icon →
Alert Preferences. Toggle any of:
- Stop-loss hits
- Take-profit hits
- Trailing stops

## 3. Bot alerts
Bots emit notifications on:
- Start / stop / completion
- Trade fills (optional — toggle per bot)
- Errors (paused due to safety rule breach, etc.)

## Push notifications (mobile)
On iPhone (installed PWA) or Android, Aanar can send **push notifications**.
Enable them in **Settings → Notifications → Enable push**. Uses Web Push API
via VAPID.
`,
  },
  {
    id: "referrals",
    title: "Referrals",
    icon: "gift",
    body: `
# Referral Program

Invite friends and earn credit toward Pro.

## How it works
1. Head to **Settings → Referrals** (or **/refer**)
2. Copy your unique code (or share link — auto-generated per user)
3. Friends who sign up with your code get **$25 virtual cash bonus**
4. **You** earn **$5 real credit** per successful signup, applied to your next Pro month

## Payout thresholds
- $5 minimum earned → auto-applies as billing credit
- $20+ earned → cash-out option (Stripe payout to your bank in supported countries)

## Fine print
- Referrals are one-shot per friend (can't self-refer)
- Friend must reach $500 total paper P&L OR remain active for 7 days for the credit to fire
- Anti-fraud checks — sockpuppet accounts get rejected
`,
  },
  {
    id: "account",
    title: "Account & Billing",
    icon: "settings",
    body: `
# Account & Billing

## Profile
- **Settings → Profile** — change name, email, avatar
- Change password anytime (or reset via **/forgot-password**)

## Two-Factor Auth (2FA)
- **Settings → Security → Enable 2FA**
- Scan the QR with Google Authenticator, Authy, or 1Password
- Generate recovery codes and store them safely
- Once enabled, every sign-in requires your 6-digit TOTP code

## Face ID (iPhone)
On installed iOS PWA, **Settings → Security → Add Face ID passkey** enrolls
your face. Future sign-ins tap "Sign in with Face ID" instead of typing your
password.

## Pricing tiers
- **Free** — $100k paper, 10 AI Coach msgs/day, 3 bots max
- **Pro** ($19/mo) — unlimited AI, unlimited bots, live IBKR routing, options, backtest
- **Founder's lifetime** — one-time payment, all Pro features forever

## Managing subscription
- **Settings → Billing** → **Manage plan**
- Cancel any time — you keep Pro until end of current billing cycle
- Auto-renewal can be turned off without cancelling

## Delete account
- **Settings → Account → Delete my account** (bottom of the page)
- 30-day grace period — sign back in during that window to restore
- After 30 days, all data is permanently purged
`,
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    icon: "life-buoy",
    body: `
# Troubleshooting

## "Add to Home Screen isn't working on iPhone"
This is an **iOS Safari restriction** — apps can't trigger Add to Home Screen
programmatically. You must:
1. Tap Safari's **Share button** in the bottom bar (the up-arrow icon)
2. Scroll and pick **Add to Home Screen**
3. Tap **Add**

The install nudge shown in-app just tells you how to do it manually — it's
not a button.

## "Live prices aren't updating"
- Check the **status pill** in the top bar. If it says "OFFLINE", tap it to reconnect
- On WiFi that blocks WebSockets, prices refresh via HTTP fallback every 15 seconds
- If IBKR is connected but prices still lag, disconnect + reconnect in Settings

## "My bot stopped unexpectedly"
Open the bot's detail page and check the **decision log**. Common causes:
- Hit its trade-count / time-window limit (completed successfully)
- Tripped a safety rule (max daily loss, max drawdown) — expected
- Broker rejected an order (insufficient buying power, symbol halted)
- Rate-limited by upstream data provider (rare, auto-retries)

## "AI Coach says 'daily limit reached'"
Free tier is 10 messages/day. Wait until midnight UTC or upgrade to Pro.

## "IBKR draft not appearing in AI Instructions tab"
- Confirm you're logged into the same IBKR account you connected
- Refresh the IBKR mobile/desktop app
- Drafts can take 30-60 seconds to propagate — wait a minute
- Still nothing? Disconnect + reconnect IBKR in Aanar Settings

## Contact support
- **In-app**: tap the "?" icon in the top bar
- **Email**: support@aanar.app
- **X / Twitter**: @aanartrading
`,
  },
];

export function buildFullManualMarkdown() {
  const header = `# Aanar — Instruction Manual\n\n*Own the market. Trade like the wizards.*\n\n---\n\n`;
  const toc = "## Contents\n\n" +
    MANUAL_SECTIONS.map((s, i) => `${i + 1}. [${s.title}](#${s.id})`).join("\n") +
    "\n\n---\n\n";
  const body = MANUAL_SECTIONS.map((s) => `<a id="${s.id}"></a>\n\n${s.body.trim()}`).join("\n\n---\n\n");
  return header + toc + body;
}
